import { supabase } from '../supabase/supabaseClient';
import { smartCache } from '../cache/smartCache';

/**
 * Message Context Metadata
 * Tracks how tickers were associated with a message
 */
export interface MessageContextMetadata {
  extracted_tickers?: string[]; // Auto-detected from message content
  manual_tickers?: string[]; // User-specified tags
  inherited_tickers?: string[]; // From reply chain or conversation window
  context_source: 'manual' | 'reply' | 'window' | 'ai' | 'explicit' | 'legacy';
  context_confidence: number; // 0-1 confidence score
  reply_context?: {
    message_id: string;
    user_name: string;
    content_preview: string;
  };
  mentioned_users?: Array<{
    // User mentions (@username)
    user_id: string;
    name: string;
    position: number;
  }>;
}

/**
 * Conversation Context Result
 * Combined ticker context from all sources
 */
export interface ConversationContext {
  tickers: string[]; // All unique tickers
  primary: string | null; // Most relevant ticker
  confidence: number; // Overall confidence score
  source: 'manual' | 'reply' | 'window' | 'ai' | 'explicit';
  sources: {
    manual: string[];
    reply: string[];
    window: string[];
    explicit: string[];
  };
}

/**
 * Conversation Window Message
 */
interface WindowMessage {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  metadata: MessageContextMetadata | null;
}

/**
 * Conversation Context Service
 * Manages context inheritance and ticker detection for group chat messages
 *
 * Context Priority:
 * 1. Manual tags (100% confidence)
 * 2. Reply chain (95% confidence)
 * 3. Explicit $TICKER in text (90% confidence)
 * 4. Conversation window (80% confidence)
 * 5. AI inference (60-80% confidence)
 */
class ConversationContextService {
  private readonly WINDOW_DURATION_MS = 10 * 60 * 1000; // 10 minutes
  private readonly MAX_REPLY_DEPTH = 3;

  /**
   * Get conversation window messages (last 10 minutes) with caching
   */
  async getConversationWindow(
    groupId: string,
    windowMs: number = this.WINDOW_DURATION_MS
  ): Promise<WindowMessage[]> {
    try {
      // Check cache first (30 second TTL for conversation windows)
      const cacheKey = `conv_window:${groupId}`;
      const cached = await smartCache.get<WindowMessage[]>(cacheKey);

      if (cached) {
        console.log(`✅ [ConversationContext] Using cached window: ${cached.length} messages`);
        return cached;
      }

      // Cache miss - fetch from DB
      const cutoffTime = new Date(Date.now() - windowMs).toISOString();

      const { data, error } = await supabase
        .from('messages')
        .select('id, user_id, content, created_at, metadata')
        .eq('group_id', groupId)
        .gte('created_at', cutoffTime)
        .order('created_at', { ascending: false })
        .limit(50); // Cap at 50 messages for performance

      if (error) {
        console.error('❌ [ConversationContext] Error fetching conversation window:', error);
        return [];
      }

      const messages = (data || []) as WindowMessage[];

      console.log(
        `🪟 [ConversationContext] Found ${messages.length} messages in last ${windowMs / 60000} minutes`
      );

      // Cache for 30 seconds
      await smartCache.set(cacheKey, messages, { ttl: 30000 });

      return messages;
    } catch (error) {
      console.error('❌ [ConversationContext] Unexpected error fetching window:', error);
      return [];
    }
  }

  /**
   * Extract tickers from conversation window
   * Returns most recent tickers with frequency count
   */
  extractTickersFromWindow(messages: WindowMessage[]): {
    tickers: string[];
    frequencies: Record<string, number>;
    mostRecent: string | null;
  } {
    const tickerMap = new Map<string, { count: number; lastSeen: Date }>();

    for (const msg of messages) {
      const allTickers = this.extractAllTickersFromMetadata(msg.metadata);

      for (const ticker of allTickers) {
        const existing = tickerMap.get(ticker);
        const timestamp = new Date(msg.created_at);

        if (!existing || timestamp > existing.lastSeen) {
          tickerMap.set(ticker, {
            count: (existing?.count || 0) + 1,
            lastSeen: timestamp,
          });
        }
      }
    }

    // Sort by last seen time (most recent first)
    const sorted = Array.from(tickerMap.entries()).sort(
      (a, b) => b[1].lastSeen.getTime() - a[1].lastSeen.getTime()
    );

    const tickers = sorted.map(([ticker]) => ticker);
    const frequencies = Object.fromEntries(sorted.map(([ticker, info]) => [ticker, info.count]));
    const mostRecent = tickers.length > 0 ? tickers[0] : null;

    console.log(
      `📊 [ConversationContext] Window tickers: ${tickers.join(', ')} | Most recent: ${mostRecent}`
    );

    return { tickers, frequencies, mostRecent };
  }

  /**
   * Get reply chain context
   * Follows reply_to_message_id up to MAX_REPLY_DEPTH levels
   */
  async getReplyChainContext(messageId: string): Promise<{
    tickers: string[];
    messages: Array<{
      id: string;
      user_name: string;
      content: string;
      tickers: string[];
      depth: number;
    }>;
  }> {
    try {
      const chain: Array<{
        id: string;
        user_name: string;
        content: string;
        tickers: string[];
        depth: number;
      }> = [];
      const allTickers = new Set<string>();

      let currentMessageId: string | null = messageId;
      let depth = 0;

      while (currentMessageId && depth < this.MAX_REPLY_DEPTH) {
        const { data, error } = await supabase
          .from('messages')
          .select(
            `
            id,
            content,
            metadata,
            reply_to_message_id,
            profiles:user_id (
              name
            )
          `
          )
          .eq('id', currentMessageId)
          .single();

        if (error || !data) {
          console.error('❌ [ConversationContext] Error fetching reply chain:', error);
          break;
        }

        const tickers = this.extractAllTickersFromMetadata(data.metadata);
        tickers.forEach(t => allTickers.add(t));

        chain.push({
          id: data.id,
          user_name: (data.profiles as any)?.name || 'Unknown',
          content: data.content,
          tickers,
          depth,
        });

        // Move to parent message
        currentMessageId = data.reply_to_message_id as string | null;
        depth++;
      }

      console.log(
        `🔗 [ConversationContext] Reply chain: ${chain.length} messages, ${allTickers.size} unique tickers`
      );

      return {
        tickers: Array.from(allTickers),
        messages: chain,
      };
    } catch (error) {
      console.error('❌ [ConversationContext] Unexpected error in reply chain:', error);
      return { tickers: [], messages: [] };
    }
  }

  /**
   * Analyze message context using hybrid approach
   *
   * Priority:
   * 1. Manual tickers (if provided)
   * 2. Reply chain context
   * 3. Conversation window
   * 4. Returns combined context
   */
  async analyzeMessageContext(
    groupId: string,
    messageContent: string,
    options: {
      manualTickers?: string[];
      replyToMessageId?: string | null;
      explicitTickers?: string[];
    }
  ): Promise<ConversationContext> {
    console.log(
      `🔍 [ConversationContext] Analyzing context for message: "${messageContent.substring(0, 50)}..."`
    );

    const sources: ConversationContext['sources'] = {
      manual: options.manualTickers || [],
      reply: [],
      window: [],
      explicit: options.explicitTickers || [],
    };

    let confidence = 0;
    let primarySource: ConversationContext['source'] = 'window';

    // Priority 1: Manual tags (highest confidence)
    if (sources.manual.length > 0) {
      confidence = 1.0;
      primarySource = 'manual';
      console.log(
        `🏷️ [ConversationContext] Manual tags: ${sources.manual.join(', ')} (confidence: 100%)`
      );
    }
    // Priority 2: Reply chain
    else if (options.replyToMessageId) {
      const replyContext = await this.getReplyChainContext(options.replyToMessageId);
      sources.reply = replyContext.tickers;

      if (sources.reply.length > 0) {
        confidence = 0.95;
        primarySource = 'reply';
        console.log(
          `🔗 [ConversationContext] Reply chain tickers: ${sources.reply.join(', ')} (confidence: 95%)`
        );
      }
    }

    // Priority 3: Explicit $TICKER in text
    if (sources.explicit.length > 0) {
      if (confidence === 0) {
        confidence = 0.9;
        primarySource = 'explicit';
      }
      console.log(
        `💬 [ConversationContext] Explicit tickers: ${sources.explicit.join(', ')} (confidence: 90%)`
      );
    }

    // Priority 4: Conversation window (if no higher priority context found)
    if (confidence < 0.9) {
      const windowMessages = await this.getConversationWindow(groupId);
      const windowContext = this.extractTickersFromWindow(windowMessages);
      sources.window = windowContext.tickers;

      if (sources.window.length > 0 && confidence === 0) {
        confidence = 0.8;
        primarySource = 'window';
        console.log(
          `🪟 [ConversationContext] Window tickers: ${sources.window.join(', ')} (confidence: 80%)`
        );
      }
    }

    // Combine all sources and deduplicate
    const allTickers = [
      ...sources.manual,
      ...sources.reply,
      ...sources.explicit,
      ...sources.window,
    ];
    const uniqueTickers = Array.from(new Set(allTickers));

    // Determine primary ticker (first from highest priority source)
    let primary: string | null = null;
    if (sources.manual.length > 0) primary = sources.manual[0];
    else if (sources.reply.length > 0) primary = sources.reply[0];
    else if (sources.explicit.length > 0) primary = sources.explicit[0];
    else if (sources.window.length > 0) primary = sources.window[0];

    console.log(
      `✅ [ConversationContext] Final context: ${uniqueTickers.join(', ')} | Primary: ${primary} | Source: ${primarySource} | Confidence: ${(confidence * 100).toFixed(0)}%`
    );

    return {
      tickers: uniqueTickers,
      primary,
      confidence,
      source: primarySource,
      sources,
    };
  }

  /**
   * Calculate context confidence based on multiple factors
   */
  calculateContextConfidence(context: ConversationContext): number {
    let confidence = context.confidence;

    // Boost confidence if multiple sources agree
    const sourcesWithTickers = Object.values(context.sources).filter(s => s.length > 0).length;
    if (sourcesWithTickers > 1) {
      confidence = Math.min(1.0, confidence + 0.05 * (sourcesWithTickers - 1));
    }

    // Reduce confidence if too many tickers (ambiguous)
    if (context.tickers.length > 3) {
      confidence *= 0.9;
    }

    return Math.round(confidence * 100) / 100;
  }

  /**
   * Extract all tickers from message metadata
   */
  private extractAllTickersFromMetadata(metadata: MessageContextMetadata | null): string[] {
    if (!metadata) return [];

    const tickers: string[] = [];

    if (metadata.extracted_tickers) {
      tickers.push(...metadata.extracted_tickers);
    }
    if (metadata.manual_tickers) {
      tickers.push(...metadata.manual_tickers);
    }
    if (metadata.inherited_tickers) {
      tickers.push(...metadata.inherited_tickers);
    }

    return Array.from(new Set(tickers)); // Deduplicate
  }

  /**
   * Format context metadata for database storage
   */
  formatContextMetadata(
    context: ConversationContext,
    replyToMessageId?: string | null,
    replyContext?: { user_name: string; content_preview: string }
  ): MessageContextMetadata {
    return {
      extracted_tickers: context.sources.explicit,
      manual_tickers: context.sources.manual,
      inherited_tickers: [...context.sources.reply, ...context.sources.window],
      context_source: context.source,
      context_confidence: this.calculateContextConfidence(context),
      reply_context:
        replyToMessageId && replyContext
          ? {
              message_id: replyToMessageId,
              user_name: replyContext.user_name,
              content_preview: replyContext.content_preview,
            }
          : undefined,
    };
  }
}

// Export singleton instance
export const conversationContextService = new ConversationContextService();
